import axios from 'axios';
import moment from 'moment-timezone';

import { getSchedulingServerURL } from '../../HarverPages/shared/helper';
import { INTERVIEW_STATUS } from '../shared/domains';

export interface Slot {
    start: string;
    end: string;
    participants: { cronofyAccountId: string }[];
}

interface GroupedAvailableSlots {
    [key: string]: Slot[];
}

interface ScheduleInterviewResponse {
    hasScheduled: boolean;
    isSlotAvailable: boolean;
    newSlots: Slot[];
}

interface ScheduleInterviewStatus {
    isSlotAvailable: boolean;
    hasScheduled: boolean;
    slots: GroupedAvailableSlots | null;
}

interface SchedulerInput {
    calendarEventId: string;
    startTime: string;
    endTime: string;
    participants: string[];
    candidatePreferredTimezone: string;
}

export enum WeekdayTypes {
    SHORT = 'short',
    LONG = 'long',
}

function groupAvailableSlotsByDate(slots: Slot[], timezoneId: string): GroupedAvailableSlots | null {
    if (slots.length === 0) return null;

    let key = '';
    return slots.reduce((acc, slot) => {
        key = moment.tz(slot.start, timezoneId).format('ddd, DD MMMM YYYY');

        if (acc.hasOwnProperty(key)) {
            acc[key].push(slot);
        } else {
            acc[key] = [slot];
        }

        return acc;
    }, {});
}

export function regroupSlotsByTimezone(
    groupedAvailableSlots: GroupedAvailableSlots,
    timezoneId: string,
): GroupedAvailableSlots | null {
    const slots: Slot[] = [];

    Object.keys(groupedAvailableSlots).forEach((key) => {
        slots.push(...groupedAvailableSlots[key]);
    });

    if (!slots.length) return null;

    return groupAvailableSlotsByDate(slots, timezoneId);
}

export interface GetAvailableSlotsResponse {
    slots: Slot[];
    interviewDuration: number;
    interviewStatus: INTERVIEW_STATUS;
}
export interface TimePickerLinkStatus extends Omit<GetAvailableSlotsResponse, 'slots'> {
    slots: GroupedAvailableSlots | null;
}
export async function getAvailableSlots(calendarEventId: string, timezoneId: string): Promise<TimePickerLinkStatus> {
    const { data } = await axios.get<GetAvailableSlotsResponse>(
        `${getSchedulingServerURL()}/interview/available-slots`,
        {
            params: { ref: calendarEventId },
        },
    );

    return { ...data, slots: groupAvailableSlotsByDate(data.slots, timezoneId) };
}

export async function scheduleInterview(input: SchedulerInput, timezoneId: string): Promise<ScheduleInterviewStatus> {
    const { data } = await axios.post<ScheduleInterviewResponse>(
        `${getSchedulingServerURL()}/interview/schedule`,
        input,
    );

    return {
        slots: groupAvailableSlotsByDate(data.newSlots, timezoneId),
        hasScheduled: data.hasScheduled,
        isSlotAvailable: data.isSlotAvailable,
    };
}

export function formatTime(dateString: string, locale: string) {
    if (locale === 'en_US') {
        return moment(dateString).format('h:mm A');
    }
    return moment(dateString).format('HH:mm');
}

export function formatTimeSlot(
    slot: { start: string; end: string },
    timezoneId: string,
    locale: string,
): { startTime: string; endTime: string } {
    if (locale == 'en_US') {
        return {
            startTime: moment.tz(slot.start, timezoneId).format('h:mm A'),
            endTime: moment.tz(slot.end, timezoneId).format('h:mm A'),
        };
    }
    return {
        startTime: moment.tz(slot.start, timezoneId).format('HH:mm'),
        endTime: moment.tz(slot.end, timezoneId).format('HH:mm'),
    };
}

export function formatDay(day: string, timezoneId: string | undefined, locale, weekdayType: WeekdayTypes): string {
    return new Date(day).toLocaleDateString(locale.replace('_', '-'), {
        timeZone: timezoneId,
        weekday: weekdayType,
        month: 'long',
        day: 'numeric',
    });
}
